import { metrics } from '@opentelemetry/api'

import { INSTRUMENTATION_NAME } from '~/utils/observability/constants'

import { normalizePathname } from './utils/normalizePathname'

/**
 * Set up meaningful distributions for HTTP latency, as the default
 * values were overly skewed toward lower percentiles.
 */
const API_REQUEST_BUCKET_BOUNDARIES = [0, 50, 100, 250, 500, 750, 1000, 2500, 5000, 10000, 25000, 60000, 120000]

let requestDuration: ReturnType<ReturnType<typeof metrics.getMeter>['createHistogram']> | undefined

function getRequestDuration() {
    if (!requestDuration) {
        const meter = metrics.getMeter(INSTRUMENTATION_NAME)
        requestDuration = meter.createHistogram('app.api.request_duration', {
            description: 'Duration of API requests by method, URL path, and status code',
            advice: { explicitBucketBoundaries: API_REQUEST_BUCKET_BOUNDARIES },
            unit: 'ms',
        })
    }
    return requestDuration
}

/**
 * Wraps the global fetch function to record request duration and status as Otel metrics.
 */
export const trackedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> = async (input, init) => {
    const isRequest = input instanceof Request
    const baseRequest = isRequest ? input : null
    const method = init?.method ?? baseRequest?.method ?? 'GET'
    const pathname = normalizePathname(baseRequest?.url ?? String(input))

    const startTime = performance.now()

    const recordRequest = (statusCode?: number) => {
        getRequestDuration().record(performance.now() - startTime, {
            'di.http.method': method,
            'di.http.pathname': pathname,
            ...(statusCode !== undefined ? { 'di.http.status_code': statusCode } : { 'di.http.error': 'network_failure' }),
        })
    }

    try {
        const response = await globalThis.fetch(input, init)
        recordRequest(response.status)
        return response
    } catch (err) {
        recordRequest()
        throw err
    }
}
